import { useEffect } from "react";

/**
 * SEO — sets <title> and injects per-route meta tags into <head>.
 * Idempotent: each meta tag is keyed by name/property + data-jh-seo so it can be
 * replaced cleanly on route change.
 *
 * For static pre-rendering: the meta tags rendered by this hook will end up in
 * the page's HTML when each route is snapshotted with Playwright + react-snap.
 */
const META_KEYS = [
  ["name", "description"],
  ["property", "og:title"],
  ["property", "og:description"],
  ["property", "og:image"],
  ["property", "og:url"],
  ["property", "og:type"],
  ["name", "twitter:title"],
  ["name", "twitter:description"],
  ["name", "twitter:image"],
  ["name", "twitter:card"],
];

function setMeta(attr, key, value) {
  if (!value) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"][data-jh-seo]`);
  if (!el) {
    el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (el && !el.hasAttribute("data-jh-seo")) {
      el.setAttribute("data-jh-seo", "");
    }
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.setAttribute("data-jh-seo", "");
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function setCanonical(url) {
  if (!url) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
}

export default function SEO({
  title,
  description,
  canonical,
  image = "https://storage.googleapis.com/jobhacki-assets/site/og/og-default.jpg",
  type = "website",
}) {
  useEffect(() => {
    if (title) document.title = title;

    const ogUrl = canonical || (typeof window !== "undefined" ? window.location.href : undefined);

    const values = {
      description,
      "og:title": title,
      "og:description": description,
      "og:image": image,
      "og:url": ogUrl,
      "og:type": type,
      "twitter:title": title,
      "twitter:description": description,
      "twitter:image": image,
      "twitter:card": "summary_large_image",
    };

    META_KEYS.forEach(([attr, key]) => setMeta(attr, key, values[key]));
    setCanonical(canonical || ogUrl);
  }, [title, description, canonical, image, type]);

  return null;
}
